import React, { useState, useEffect } from 'react';
import { DynamicSurveyResponse, SurveyDefinition } from '../../types/survey.types';
import { useResponses } from '../../hooks/useResponses';

interface ResponseListProps {
  surveyId: string;
  survey: SurveyDefinition;
}

const ResponseList: React.FC<ResponseListProps> = ({ surveyId, survey }) => {
  const { getResponses, loading } = useResponses();
  const [responses, setResponses] = useState<DynamicSurveyResponse[]>([]);
  const [filteredResponses, setFilteredResponses] = useState<DynamicSurveyResponse[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedResponse, setSelectedResponse] = useState<DynamicSurveyResponse | null>(null);
  const responsesPerPage = 15;

  useEffect(() => {
    loadResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  useEffect(() => {
    filterResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, responses]);

  const loadResponses = async () => {
    try {
      const data = await getResponses(surveyId);
      setResponses(data);
      setFilteredResponses(data);
    } catch (error) {
      console.error('[ResponseList] Error loading responses:', error);
    }
  };

  const filterResponses = () => {
    if (!searchTerm) {
      setFilteredResponses(responses);
      return;
    }

    const filtered = responses.filter(response => {
      const searchLower = searchTerm.toLowerCase();
      if (response.id?.toLowerCase().includes(searchLower)) return true;
      const responseValues = Object.values(response.responses || {});
      return responseValues.some(responseObj => {
        const value = responseObj?.value;
        if (typeof value === 'string') return value.toLowerCase().includes(searchLower);
        if (Array.isArray(value)) return value.some(v => String(v).toLowerCase().includes(searchLower));
        return false;
      });
    });

    setFilteredResponses(filtered);
    setCurrentPage(1);
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    try {
      let date: Date;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      if (isNaN(date.getTime())) return '-';
      const dateStr = date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
      const timeStr = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
      return `${dateStr} ${timeStr}`;
    } catch {
      return '-';
    }
  };

  const allQuestions = survey.sections.flatMap(section =>
    section.questions.map(q => ({ id: q.id, title: q.title, type: q.type, settings: q.settings }))
  );

  const formatResponseValue = (value: any, questionType: string): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (questionType === 'yesno') return value === true || value === 'Yes' || value === 'yes' ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    const str = String(value);
    return str.length > 30 ? str.substring(0, 30) + '...' : str;
  };

  const exportToCSV = () => {
    const headers = ['#', 'Date', ...allQuestions.map(q => q.title)];
    const rows = filteredResponses.map((response, idx) => {
      const row = [
        String(idx + 1),
        formatDate(response.metadata?.submittedAt),
        ...allQuestions.map(q => {
          const value = response.responses[q.id]?.value;
          if (value === null || value === undefined) return '';
          if (Array.isArray(value)) return value.join('; ');
          return String(value);
        })
      ];
      return row;
    });

    const csvContent = [
      headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `${survey.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_responses.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const indexOfLastResponse = currentPage * responsesPerPage;
  const indexOfFirstResponse = indexOfLastResponse - responsesPerPage;
  const currentResponses = filteredResponses.slice(indexOfFirstResponse, indexOfLastResponse);
  const totalPages = Math.ceil(filteredResponses.length / responsesPerPage);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 mx-auto" style={{ borderBottom: '2px solid var(--color-accent)', borderTop: '2px solid transparent', borderLeft: '2px solid transparent', borderRight: '2px solid transparent' }}></div>
        <p className="mt-4 text-sm" style={{ color: 'var(--color-muted)' }}>Loading responses...</p>
      </div>
    );
  }

  if (responses.length === 0) {
    return (
      <div className="text-center py-12">
        <p style={{ fontSize: '1rem', color: 'var(--color-muted)' }}>No responses yet</p>
      </div>
    );
  }

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-1.5 rounded-lg border text-sm flex-1 max-w-xs"
            style={{ borderColor: 'var(--glass-border-light)', backgroundColor: 'white' }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-xs px-2 py-1 rounded"
              style={{ color: 'var(--color-muted)' }}
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {filteredResponses.length} responses
          </span>
          <button
            onClick={exportToCSV}
            className="text-xs font-medium px-3 py-1.5 rounded-md"
            style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--glass-border-light)' }}>
        <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th className="px-2 py-2 text-left sticky left-0" style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: 'var(--color-muted)',
                  textTransform: 'uppercase',
                  width: '40px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid var(--color-accent)',
                  zIndex: 20
                }}>
                  #
                </th>
                <th className="px-2 py-2 text-left" style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: 'var(--color-muted)',
                  textTransform: 'uppercase',
                  minWidth: '110px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid var(--color-accent)'
                }}>
                  Date/Time
                </th>
                {allQuestions.map((q, i) => (
                  <th key={q.id} className="px-2 py-2 text-left align-bottom" style={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: 'var(--color-primary)',
                    minWidth: '80px',
                    maxWidth: '120px',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid var(--color-accent)',
                    whiteSpace: 'normal',
                    lineHeight: '1.3',
                    verticalAlign: 'bottom'
                  }} title={q.title}>
                    <div>
                      {q.title}
                      {/* Scale indicator for rating/scale questions */}
                      {['rating', 'scale'].includes(q.type) && (
                        <div
                          style={{
                            marginTop: '2px',
                            fontSize: '0.625rem',
                            fontWeight: 500,
                            color: '#b8860b',
                            backgroundColor: 'rgba(199, 161, 74, 0.15)',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            display: 'inline-block'
                          }}
                        >
                          Scale: {q.settings?.minValue ?? 1}-{q.settings?.maxValue ?? 5}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-2 py-2 text-center sticky right-0" style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: 'var(--color-muted)',
                  textTransform: 'uppercase',
                  width: '50px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid var(--color-accent)',
                  zIndex: 20
                }}>

                </th>
              </tr>
            </thead>
            <tbody>
              {currentResponses.map((response, index) => (
                <tr
                  key={response.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                    borderBottom: '1px solid rgba(0,0,0,0.05)'
                  }}
                  className="hover:bg-amber-50 transition-colors"
                >
                  <td className="px-2 py-1.5 sticky left-0" style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-accent)',
                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                    zIndex: 10
                  }}>
                    {indexOfFirstResponse + index + 1}
                  </td>
                  <td className="px-2 py-1.5" style={{
                    fontSize: '0.6875rem',
                    color: 'var(--color-muted)'
                  }}>
                    {formatDate(response.metadata?.submittedAt)}
                  </td>
                  {allQuestions.map(q => (
                    <td key={q.id} className="px-2 py-1.5 align-top" style={{
                      fontSize: '0.75rem',
                      color: 'var(--color-primary)',
                      minWidth: '80px',
                      maxWidth: '120px',
                      lineHeight: '1.3'
                    }} title={String(response.responses[q.id]?.value || '')}>
                      {formatResponseValue(response.responses[q.id]?.value, q.type)}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center sticky right-0" style={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                    zIndex: 10
                  }}>
                    <button
                      onClick={() => setSelectedResponse(response)}
                      className="text-xs font-medium px-2 py-0.5 rounded"
                      style={{ color: 'var(--color-accent)' }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-40"
            style={{ backgroundColor: 'rgba(0,0,0,0.05)', color: 'var(--color-muted)' }}
          >
            Prev
          </button>
          <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-40"
            style={{ backgroundColor: 'rgba(0,0,0,0.05)', color: 'var(--color-muted)' }}
          >
            Next
          </button>
        </div>
      )}

      {/* Response Detail Modal */}
      {selectedResponse && (
        <ResponseDetailModal
          response={selectedResponse}
          survey={survey}
          responseNumber={responses.findIndex(r => r.id === selectedResponse.id) + 1}
          onClose={() => setSelectedResponse(null)}
        />
      )}
    </div>
  );
};

interface ResponseDetailModalProps {
  response: DynamicSurveyResponse;
  survey: SurveyDefinition;
  responseNumber: number;
  onClose: () => void;
}

const ResponseDetailModal: React.FC<ResponseDetailModalProps> = ({ response, survey, responseNumber, onClose }) => {
  const formatDateTime = (timestamp: any): string => {
    if (!timestamp) return '-';
    try {
      let date: Date;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      if (isNaN(date.getTime())) return '-';
      const dateStr = date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
      return `${dateStr} at ${timeStr}`;
    } catch {
      return '-';
    }
  };

  const formatValue = (value: any, questionType: string): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (questionType === 'yesno') return value === true || value === 'Yes' || value === 'yes' ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  // Get all questions with their answers
  const allQA = survey.sections.flatMap(section =>
    section.questions.map(q => ({
      question: q.title,
      answer: formatValue(response.responses?.[q.id]?.value, q.type),
      required: q.required
    }))
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        style={{ maxHeight: '70vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Compact Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)', backgroundColor: '#f8f9fa' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
              style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}
            >
              {responseNumber}
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>Response #{responseNumber}</p>
              <p className="text-xs" style={{ color: 'var(--color-muted)' }}>{formatDateTime(response.metadata?.submittedAt)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
            style={{ color: 'var(--color-muted)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Compact Two-Column Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 100px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {allQA.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                  <td className="px-3 py-2 align-top" style={{
                    fontSize: '0.6875rem',
                    color: 'var(--color-muted)',
                    width: '45%',
                    lineHeight: '1.3'
                  }}>
                    {item.question}
                  </td>
                  <td className="px-3 py-2 align-top" style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-primary)',
                    fontWeight: 500,
                    lineHeight: '1.3'
                  }}>
                    {item.answer}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Minimal Footer */}
        <div className="px-4 py-2 border-t text-center" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="text-xs font-medium px-4 py-1.5 rounded transition-colors"
            style={{ color: 'var(--color-accent)' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResponseList;
